import { useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { shiftMonth } from "./callForm";
import { errorMessage, monthName, recordedLabel } from "./format";
import {
  dotPosition,
  factorChanges,
  marketFactorsFromCodes,
  notesOf,
  positionLabel,
  positionsOf,
  pressureVerdict,
  type ChangeDirection,
  type MarketFactor,
} from "./marketViewLogic";
import { MarketViewForm } from "./MarketViewForm";
import { SectionHeader } from "./SectionHeader";
import { TeamView } from "./TeamView";
import type { MarketBriefData, MarketView, RefCode } from "./types";

interface Props {
  month: string;
  resinFamilyCode: string;
  canWrite: boolean;
}

interface LoadedBrief {
  loadKey: string;
  brief: MarketBriefData;
}

const EMPTY_TEXT_CLASSES = "m-0 text-rs-light-gray text-[13px]";

const CHANGE_DISPLAY: Record<ChangeDirection, { icon: string; iconClass: string; chipText: string }> = {
  towardBuyers: { icon: "trending_down", iconClass: "text-rs-green", chipText: "Toward buyers" },
  towardSellers: { icon: "trending_up", iconClass: "text-rs-red", chipText: "Toward sellers" },
  newlySet: { icon: "add", iconClass: "text-rs-light-gray", chipText: "Newly set" },
  cleared: { icon: "remove", iconClass: "text-rs-light-gray", chipText: "Cleared" },
};

export function MarketBrief({ month, resinFamilyCode, canWrite }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const loadKey = `${resinFamilyCode}|${month}`;
  const [factors, setFactors] = useState<MarketFactor[] | null>(null);
  const [factorsLoadError, setFactorsLoadError] = useState<string | null>(null);
  const [loadedBrief, setLoadedBrief] = useState<LoadedBrief | null>(null);
  const [briefLoadError, setBriefLoadError] = useState<string | null>(null);
  const [teamRefreshCount, setTeamRefreshCount] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const loadError = factorsLoadError ?? briefLoadError;

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current("reference/MarketFactor")
      .then((response) => response.json())
      .then((codes: RefCode[]) => {
        if (!isStale) setFactors(marketFactorsFromCodes(codes));
      })
      .catch((error) => {
        if (!isStale) setFactorsLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, []);

  useEffect(() => {
    let isStale = false;
    apiFetchRef
      .current(
        `consultant-cockpit/market-views/brief?resinFamily=${encodeURIComponent(resinFamilyCode)}&month=${month}-01`,
      )
      .then((response) => response.json())
      .then((brief: MarketBriefData) => {
        if (isStale) return;
        setLoadedBrief({ loadKey, brief });
        setBriefLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setBriefLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, loadKey]);

  const brief = loadedBrief?.loadKey === loadKey ? loadedBrief.brief : null;
  const current = brief?.current ?? null;
  const previous = brief?.previous ?? null;
  const currentPositions = useMemo(() => positionsOf(current), [current]);
  const previousPositions = useMemo(() => positionsOf(previous), [previous]);
  const currentNotes = useMemo(() => notesOf(current), [current]);
  const previousNotes = useMemo(() => notesOf(previous), [previous]);
  const verdict = pressureVerdict(currentPositions);
  const monthLabel = monthName(month);
  const lastMonthLabel = monthName(shiftMonth(month, -1));

  const handleSaved = (savedView: MarketView) => {
    if (brief) setLoadedBrief({ loadKey, brief: { ...brief, current: savedView } });
    setTeamRefreshCount((count) => count + 1);
  };

  return (
    <section className="lane on" aria-labelledby="brief-heading">
      <SectionHeader sectionId="brief-section">
        <Chip>{monthLabel}</Chip>
        <Chip variant="ok">{resinFamilyCode}</Chip>
      </SectionHeader>

      {loadError ? (
        <Card>
          <p className={EMPTY_TEXT_CLASSES}>Couldn't load the market brief: {loadError}</p>
        </Card>
      ) : !brief || !factors ? (
        <Card>
          <p className={EMPTY_TEXT_CLASSES}>Loading…</p>
        </Card>
      ) : (
        <>
          <Card className="flex flex-wrap items-center gap-6">
            <div className="flex flex-1 min-w-0 flex-col gap-3">
              {verdict && current ? (
                <>
                  <Chip variant="ok" className="self-start font-semibold">
                    {verdict.lean}
                  </Chip>
                  <p className="m-0 text-[28px] leading-tight font-semibold text-white">
                    {current.summary || "No one-line view saved."}
                  </p>
                  <p className={EMPTY_TEXT_CLASSES}>
                    {verdict.buyerCount} of {verdict.setCount} factors favor buyers, {verdict.sellerCount} favor
                    sellers. Your {monthLabel} {resinFamilyCode} market view, saved {recordedLabel(current.recordedUtc)}.
                  </p>
                </>
              ) : (
                <>
                  <Chip className="self-start">No market view yet</Chip>
                  <p className="m-0 text-[28px] leading-tight font-semibold text-white">
                    Build the {monthLabel} {resinFamilyCode} view.
                  </p>
                  <p className={EMPTY_TEXT_CLASSES}>
                    {current
                      ? `Your ${monthLabel} ${resinFamilyCode} market view has no factor positions.`
                      : canWrite
                        ? "Click Build your view to fill it in and save it."
                        : "No market view saved."}{" "}
                    It shows here once saved.
                  </p>
                </>
              )}
            </div>
            {canWrite && !isFormOpen && (
              <Btn onClick={() => setIsFormOpen(true)}>
                <span className="material-symbols-sharp">edit</span>
                {current ? "Update your view" : "Build your view"}
              </Btn>
            )}
          </Card>

          {isFormOpen && (
            <MarketViewForm
              key={loadKey}
              month={month}
              resinFamilyCode={resinFamilyCode}
              factors={factors}
              current={current}
              previous={previous}
              canWrite={canWrite}
              onSaved={handleSaved}
              onClose={() => setIsFormOpen(false)}
            />
          )}

          <div className="grid grid-cols-2 max-[1100px]:grid-cols-1 gap-6 mt-4">
            <Card>
              <div className="flex flex-wrap items-center gap-2.5 mb-2.5">
                <h3 className="m-0 text-[15px]">The balance of pressure</h3>
                {verdict && (
                  <Chip>
                    {verdict.buyerCount} of {verdict.setCount} favor buyers
                  </Chip>
                )}
              </div>
              {!verdict ? (
                <p className={EMPTY_TEXT_CLASSES}>
                  No {resinFamilyCode} factor positions saved for {monthLabel}.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-[140px_1fr] gap-3 mb-1.5 text-[11px] uppercase tracking-[0.06em] text-rs-light-gray">
                    <span />
                    <div className="flex justify-between">
                      <span>Favors sellers</span>
                      <span>Neutral</span>
                      <span>Favors buyers</span>
                    </div>
                  </div>
                  {factors.map((factor) => {
                    const thisMonthScore = currentPositions[factor.code];
                    const lastMonthScore = previousPositions[factor.code];
                    return (
                      <div
                        key={factor.code}
                        className="grid grid-cols-[140px_1fr] gap-3 items-center py-[7px] border-t border-rs-jet text-[13px]"
                      >
                        <span title={factor.hint}>{factor.name}</span>
                        <div
                          className="relative h-[18px]"
                          role="img"
                          aria-label={`${factor.name}: ${monthLabel} ${positionLabel(thisMonthScore)}${
                            previous ? `, ${lastMonthLabel} ${positionLabel(lastMonthScore)}` : ""
                          }`}
                        >
                          <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-linear-to-r from-rs-red via-rs-jet-light to-rs-green opacity-60" />
                          <span className="absolute left-1/2 top-0.5 bottom-0.5 w-px bg-rs-jet-light" />
                          {lastMonthScore !== undefined && (
                            <PressureDot
                              score={lastMonthScore}
                              label={`${lastMonthLabel}: ${positionLabel(lastMonthScore)}`}
                              note={previousNotes[factor.code]}
                              dotClassName="border-2 border-rs-light-gray bg-rs-off-black"
                            />
                          )}
                          {thisMonthScore !== undefined && (
                            <PressureDot
                              score={thisMonthScore}
                              label={`${
                                thisMonthScore === lastMonthScore ? `${monthLabel} and ${lastMonthLabel}` : monthLabel
                              }: ${positionLabel(thisMonthScore)}`}
                              note={currentNotes[factor.code]}
                              dotClassName="bg-white shadow-[0_0_0_3px_var(--color-rs-off-black)]"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex gap-4 mt-2.5 text-xs text-rs-light-gray">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-rs-light-gray" />
                      {previous ? lastMonthLabel : `No ${lastMonthLabel} view`}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-white" />
                      {monthLabel}
                    </span>
                  </div>
                </>
              )}
            </Card>

            <Card>
              <h3 className="m-0 mb-2.5 text-[15px]">What changed since last month</h3>
              {!current || !previous ? (
                <p className={EMPTY_TEXT_CLASSES}>
                  Nothing to compare yet. Changes show once {resinFamilyCode} market views are saved for both{" "}
                  {lastMonthLabel} and {monthLabel}.
                </p>
              ) : (
                <ChangesList factors={factors} current={current} previous={previous} lastMonthLabel={lastMonthLabel} />
              )}
            </Card>
          </div>

          <TeamView
            month={month}
            resinFamilyCode={resinFamilyCode}
            factors={factors}
            refreshCount={teamRefreshCount}
          />
        </>
      )}
    </section>
  );
}

interface PressureDotProps {
  score: number;
  label: string;
  note?: string;
  dotClassName: string;
}

function PressureDot({ score, label, note, dotClassName }: PressureDotProps) {
  return (
    <span
      className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 p-1 cursor-default"
      style={{ left: dotPosition(score) }}
    >
      <span className={`block w-3 h-3 rounded-full ${dotClassName}`} />
      <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 w-max max-w-64 -translate-x-1/2 rounded border border-rs-jet-light bg-rs-black px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100">
        <span className="block whitespace-nowrap">{label}</span>
        {note && <span className="block mt-0.5 text-rs-light-gray">{note}</span>}
      </span>
    </span>
  );
}

interface ChangesListProps {
  factors: MarketFactor[];
  current: MarketView;
  previous: MarketView;
  lastMonthLabel: string;
}

function ChangesList({ factors, current, previous, lastMonthLabel }: ChangesListProps) {
  const changes = factorChanges(factors, current, previous);
  if (changes.length === 0) {
    return <p className={EMPTY_TEXT_CLASSES}>No factor moved since {lastMonthLabel}.</p>;
  }
  return (
    <ul className="list-none m-0 p-0 flex flex-col gap-2">
      {changes.map((change) => {
        const display = CHANGE_DISPLAY[change.direction];
        return (
          <li
            key={change.factor.code}
            className="grid grid-cols-[22px_minmax(0,1fr)_auto] gap-2.5 items-baseline pb-2 border-b border-rs-jet text-[13.5px]"
          >
            <span className={`material-symbols-sharp self-center text-[18px] ${display.iconClass}`} aria-hidden="true">
              {display.icon}
            </span>
            <span>
              <b className="text-white">{change.factor.name}</b>: {positionLabel(change.previousScore)} →{" "}
              {positionLabel(change.currentScore)}
              {change.note && <small className="block text-rs-light-gray">{change.note}</small>}
            </span>
            <Chip>{display.chipText}</Chip>
          </li>
        );
      })}
    </ul>
  );
}
